import { useCallback } from 'react';
import { Domingo } from '@fiverr-private/domingo';
import pathfinder from '@fiverr-private/pathfinder';
import { getContext } from '@fiverr-private/fiverr_context';
import { SocialProvider } from '../../components/SocialButton/constants';
import windowManager from '../../utils/windowManager';
import { logError } from '../../utils/logger';
import { globalGrafanaReporter } from '../../events/grafana';
import { AuthenticationResponse, AuthenticationPostMessagePayload } from '../../shared/constants/authenticationFlow';
import { AUTHENTICATION_EVENT, WINDOW_NAME } from './constants';

declare global {
    interface WindowEventMap {
        socialAuthenticationMessage: CustomEvent<AuthenticationResponse>;
    }
}

interface UseSocialButtonClickHandlerProps {
    provider: SocialProvider;
    onClick?: () => void;
    onSuccess?: (provider: SocialProvider, authenticationResponse: AuthenticationResponse) => void;
    onError?: (provider: SocialProvider, authenticationResponse: AuthenticationResponse) => void;
}

export const useSocialButtonClickHandler = ({
    provider,
    onClick,
    onSuccess,
    onError,
}: UseSocialButtonClickHandlerProps) => {
    const onSocialButtonClick = useCallback(() => {
        const handleSocialAuthenticationPostMessage = (event: MessageEvent<AuthenticationPostMessagePayload>) => {
            if (event.data.name !== AUTHENTICATION_EVENT) {
                return;
            }

            const authOrigin = Domingo(window.origin).auth().removePath().url;

            // ! Failure to check the origin property enables cross-site scripting attacks !
            if (![authOrigin, window.origin].includes(event.origin)) {
                globalGrafanaReporter.count('social_signing.%{platform}.post_message_callback.wrong_origin');
                logError(`Received a "${AUTHENTICATION_EVENT}" post message from an unexpected origin`, {
                    origin: event.origin,
                });
                return;
            }

            // ! Failure to check the source property enables cross-site scripting attacks !
            if (event.source !== windowManager.current) {
                globalGrafanaReporter.count('social_signing.%{platform}.post_message_callback.wrong_source');
                return;
            }

            const { response: authenticationResponse } = event.data;
            const { result_hash: result } = authenticationResponse;

            windowManager.close();
            window.removeEventListener('message', handleSocialAuthenticationPostMessage);

            if (result.status === 'success') {
                onSuccess?.(provider, authenticationResponse);
                return;
            }

            if (result.status === 'failure' || result.error) {
                onError?.(provider, authenticationResponse);
            }
        };

        window.addEventListener('message', handleSocialAuthenticationPostMessage);

        onClick?.();

        const { isMobile, isAndroid } = getContext();
        const authUrl =
            isMobile && isAndroid
                ? Domingo(window.origin).auth().path('auth_redirect', { provider }).url
                : pathfinder('auth_redirect', { provider });

        windowManager.open(authUrl, WINDOW_NAME);
    }, [provider, onClick, onSuccess, onError]);

    return {
        onSocialButtonClick,
    };
};
